import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {Map<string,string>} */
  static quickAddSectionCache = new Map();
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.handleCombinedProductClick.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  async variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
      }),
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const isOnQuickAdd = !!event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // If inside quick add dialog, refresh the modal content via Sections API
    if (isOnQuickAdd) {
      await this.#updateQuickAddModalFromSection(selectedOption);
    } else {
      this.fetchUpdatedSection(
        this.buildRequestUrl(selectedOption),
        loadsNewProduct,
      );
    }

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Fetches the product-quick-add section for the selected variant and morphs the quick add modal.
   * Also dispatches VariantUpdateEvent so listeners (dialog) can update links.
   * @param {HTMLElement} selectedOption
   */
  async #updateQuickAddModalFromSection(selectedOption) {
    try {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (!modalContent) return;

      // Determine base product URL (supports combined listings)
      let productUrl =
        selectedOption.dataset.connectedProductUrl || this.dataset.productUrl;
      if (!productUrl) return;

      const variantId = selectedOption.dataset.variantId || null;
      const u = new URL(productUrl, window.location.origin);
      if (variantId) {
        u.searchParams.set('variant', variantId);
      } else {
        u.searchParams.delete('variant');
      }
      u.searchParams.set('section_id', 'product-quick-add');

      // Simple in-memory cache (module-level singleton)
      const cacheKey = u.toString();
      const cache = VariantPicker.quickAddSectionCache;

      let htmlText = cache.get(cacheKey);
      if (!htmlText) {
        const response = await fetch(cacheKey, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) return;
        htmlText = await response.text();
        cache.set(cacheKey, htmlText);
      }

      const doc = new DOMParser().parseFromString(htmlText, 'text/html');

      const gridElement = doc.querySelector('[data-product-grid-content]');
      if (!gridElement) return;

      // Display content same as desktop: no mobile-specific rearrangement

      // Morph using a clone so cached HTML remains a pristine string source
      morph(modalContent, gridElement.cloneNode(true));

      // Reinitialize any media-gallery components inside the modal
      requestYieldCallback(() => {
        try {
          /** @type {NodeListOf<HTMLElement>} */
          const galleries = document.querySelectorAll(
            '#quick-add-modal-content media-gallery',
          );
          galleries.forEach((el) => {
            const anyEl = /** @type {any} */ (el);
            if (typeof anyEl.reinit === 'function') anyEl.reinit();
          });
        } catch (_) {}
      });

      // Prepare and dispatch variant update payload (if present) to keep links in sync
      const textContent = doc.querySelector(
        'variant-picker script[type="application/json"]',
      )?.textContent;
      /** @type {any} */
      let variantPayload = {};
      if (textContent) {
        try {
          variantPayload = JSON.parse(textContent);
        } catch (_) {}
      }
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(variantPayload, this.selectedOptionId, {
            html: doc,
            productId: this.dataset.productId ?? '',
          }),
        );
      }
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Handles clicks on combined listing product links within the picker.
   * Prevents navigation, fetches the new product page, morphs <main>, then updates the URL.
   * @param {MouseEvent} event
   */
  handleCombinedProductClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest('a');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (!this.contains(anchor)) return;

    // Only intercept left-click without modifier keys and without explicit target (e.g., _blank)
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    if (anchor.target && anchor.target !== '_self') return;

    const href = anchor.getAttribute('href');
    if (!href) return;

    // Heuristic: limit to product links typically rendered in the set thumbnails
    try {
      const url = new URL(href, window.location.origin);
      if (!url.pathname.includes('/products/')) return;
    } catch (_) {
      return;
    }

    event.preventDefault();
    // Fetch and morph the main content
    this.fetchUpdatedSection(href, true);
    // Update browser address bar
    requestYieldCallback(() => {
      history.replaceState({}, '', href);
    });
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`,
      );

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest('quick-add-component') ||
      this.closest('swatches-variant-picker-component')
    ) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join(
        '&',
      )}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`,
        )?.textContent;

        if (shouldMorphMain) {
          this.updateMain(html);
          // Reinitialize media galleries after main morph completes
          requestYieldCallback(() => {
            try {
              /** @type {NodeListOf<HTMLElement>} */
              const galleries = document.querySelectorAll('media-gallery');
              galleries.forEach((el) => {
                const anyEl = /** @type {any} */ (el);
                if (typeof anyEl.reinit === 'function') {
                  anyEl.reinit();
                }
              });
            } catch (_) {}
          });
        } else {
          // Only attempt to update the variant picker if the response contains one
          // and we're NOT inside a product-card. Product cards handle their own
          // partial morphing to preserve swatch UI state.
          let newProduct;
          const hasVariantPicker = !!html.querySelector(
            this.tagName.toLowerCase(),
          );
          const isInsideProductCard = !!this.closest('product-card');
          if (hasVariantPicker && !isInsideProductCard) {
            newProduct = this.updateVariantPicker(html);
          }

          // Prepare variant payload if available; otherwise, use an empty object
          /** @type {any} */
          let variantPayload = {};
          if (textContent) {
            try {
              variantPayload = JSON.parse(textContent);
            } catch (_) {
              // ignore JSON parse errors and proceed with empty payload
            }
          }

          // Dispatch update event so listeners (e.g., product-card) can morph using returned HTML
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantPayload, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              }),
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase(),
    );

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked',
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked'),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
